import React from 'react';
import { 
  X, ChevronLeft, ChevronRight, User, QrCode, History, 
  HeartHandshake, Shirt, ShieldAlert, Phone, Mail, FileText, GraduationCap, UserCheck, HandFist, UserLock, ArrowLeftRight, SquareChartGantt, Captions
} from 'lucide-react';
export default function StudentRightSidebar({
  isOpen, 
  onToggle, 
  activeTab, 
  onSelectTab, 
  student, 
  counts = {} 
}) {
  const navigationItems = [
    { id: 'info', label: 'Overview & Profile', icon: User },
    { id: 'academic', label: 'Academic', icon: GraduationCap },
    { id: 'attendance', label: 'Attendance', icon: UserCheck },
    { id: 'behavior', label: 'Behavior Log', icon: HandFist },
    { id: 'departureLogs', label: 'Departure Logs', icon: History, count: counts.departureLogs },
    { id: 'dresscode', label: 'Dress Code', icon: Shirt },
    { id: 'family', label: 'Family', icon: HeartHandshake },
    { id: 'account', label: 'Login Account', icon: UserLock },
    { id: 'passCard', label: 'Pass Card', icon: QrCode },
    { id: 'conferrence', label: 'P/T Conferrence', icon: ArrowLeftRight },
    { id: 'reportcard', label: 'Report Card', icon: SquareChartGantt },
    { id: 'transcript', label: 'Transcript', icon: Captions },
  ];

  return (
    <div className="relative shrink-0">
      {/* Closed State Toggle Button */}
      {!isOpen && (
        <button
          type="button"
          onClick={onToggle}
          title="Open Menu"
          className="flex items-center gap-1.5 bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50 p-2 rounded-lg shadow-xs text-xs font-semibold cursor-pointer transition-colors"
        >
          <ChevronLeft size={16} />
          <span>Show Menu</span>
        </button>
      )}

      {/* Inline Sidebar Panel */}
      {isOpen && (
        <aside className="w-80 bg-white border border-zinc-200/80 rounded-lg shadow-2xs flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
          {/* Sidebar Header */}
          <div className="p-3.5 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/70">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-zinc-900 text-white rounded-lg">
                <User size={15} />
              </div>
              <div>
                <h3 className="text-[11px] font-bold text-zinc-900 uppercase tracking-wider">
                  Student Navigation
                </h3>
                <p className="text-[10px] text-zinc-500 truncate max-w-[150px]">
                  {student?.first_name ? `${student.first_name} ${student.last_name}` : 'Quick Controls'}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onToggle}
              className="p-1 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-200/60 rounded-md transition-colors cursor-pointer"
            >
              <X size={16} />
            </button>
          </div>

          {/* Navigation Items */}
          <div className="p-3 space-y-4">
            <div className="space-y-1">
              <p className="px-2 text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">
                Tab Sections
              </p>
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onSelectTab(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                      isActive
                        ? 'bg-zinc-900 text-white shadow-xs'
                        : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <Icon size={15} className={isActive ? 'text-white' : 'text-zinc-400'} />
                      <span>{item.label}</span>
                    </div>

                    {item.count !== undefined && (
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          isActive
                            ? 'bg-white/20 text-white'
                            : 'bg-zinc-100 text-zinc-600'
                        }`}
                      >
                        {item.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Quick Details Section */}
            <div className="p-3 bg-zinc-50 border border-zinc-200/80 rounded-lg space-y-2">
              <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                Quick Details
              </p>

              <div className="space-y-1.5 text-xs">
                <div className="flex items-center gap-2 text-zinc-600">
                  <FileText size={13} className="text-zinc-400 shrink-0" />
                  <span className="truncate">ID: <strong>{student?.student_id_number || 'N/A'}</strong></span>
                </div>
                <div className="flex items-center gap-2 text-zinc-600">
                  <Phone size={13} className="text-zinc-400 shrink-0" />
                  <span className="truncate">{student?.phone_number || 'No contact phone'}</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-600">
                  <Mail size={13} className="text-zinc-400 shrink-0" />
                  <span className="truncate">{student?.email || 'No email registered'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Collapse Button */}
          <div className="p-2.5 border-t border-zinc-100 bg-zinc-50/50">
            <button
              type="button"
              onClick={onToggle}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 px-3 bg-white border border-zinc-200/80 text-zinc-600 hover:text-zinc-900 rounded-lg text-xs font-medium shadow-2xs hover:bg-zinc-50 transition-colors cursor-pointer"
            >
              <ChevronRight size={15} />
              Hide Sidebar
            </button>
          </div>
        </aside>
      )}
    </div>
  );
}